import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Colors, Icons, Dropdown } from '@sellout/ui';
import useEvent from "../hooks/useEvent.hook";
import PageLoader from "../components/PageLoader";
import * as Time from "@sellout/utils/.dist/time";
import ITicketType from "@sellout/models/.dist/interfaces/ITicketType";
import { useQuery } from '@apollo/react-hooks';
import QUERY_ORDERS from '@sellout/models/.dist/graphql/queries/orders.query';
import IEventUpgrade from '@sellout/models/.dist/interfaces/IEventUpgrade';
import IEvent from "@sellout/models/.dist/interfaces/IEvent";
import IOrder from "@sellout/models/.dist/interfaces/IOrder";
import ITicketTier from "@sellout/models/.dist/interfaces/ITicketTier"; 
import * as Price from '@sellout/utils/.dist/price';
import IOrderTicket from '@sellout/models/.dist/interfaces/IOrderTicket';
import { PaddedPage } from '../components/PageLayout';
import Button, { ButtonTypes } from "@sellout/ui/build/components/Button";
import { EventSaleTaxEnum } from "@sellout/models/.dist/interfaces/IEvent";
import { OrderTypeEnum } from "@sellout/models/.dist/interfaces/IOrderType";
import { ModalTypes } from '../components/modal/Modal';
import { useDispatch } from 'react-redux';
import * as AppActions from "../redux/actions/app.actions";
import { media } from '@sellout/ui/build/utils/MediaQuery';
import { OrderStateEnum } from '@sellout/models/.dist/interfaces/IOrderState';
import IFee, { FeeAppliedByEnum } from '@sellout/models/.dist/interfaces/IFee';
import { PaymentMethodTypeEnum } from "@sellout/models/.dist/enums/PaymentMethodTypeEnum";
import { OrderChannelEnum } from "@sellout/models/.dist/enums/OrderChannelEnum";

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 44px;
  max-width: 1000px;
  @media print {
    display: none;
  }
  ${media.mobile`
    flex-direction: column;
  `}
`;
export const Spacer = styled.div`
 ${media.mobile`
  width: 15px;
  padding: 5px;
  `}
`;
const FilterWrapper = styled.div`
  width: 200px;
  ${media.mobile`
    width: 50%;
  `}
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 24px;
  ${media.mobile`
    display: block;
  `}
`;

const PageTitle = styled.div`
  color: ${Colors.Grey1};
  font-weight: 600;
  font-size: 2.4rem;
  
  ${media.mobile`
    padding-bottom: 15px;
  `}
`;

const TableContainer = styled.div`
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  border: 1px solid ${Colors.Grey5};
  background: ${Colors.White};
  width: 100%;
  height: fit-content;
  // max-height: calc(100% - 90px);
  overflow: scroll;
  max-width: 1000px;
`;

const SectionHeaderStyle = styled.div`
  width: 100%;
  height: 45px;
  display: flex;
  background: ${Colors.Grey6};
  align-items: center;
  padding: 0px 30px;
  box-sizing: border-box;
  min-width: 750px;
  @media print {
    min-width: 680px;
  }
`;

type SectionRowStyleProps = {
  bold?: boolean;
};

const SectionRowStyle = styled.div<SectionRowStyleProps>`
  width: 100%;
  height: 45px;
  display: flex;
  background: ${Colors.White};
  align-items: center;
  padding: 0px 30px;
  box-sizing: border-box;
  font-weight: ${props => props.bold ? '600' : '500'};
  min-width: 750px;
  @media print {
    min-width: 680px;
  }
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 1.2rem;
  color: ${Colors.Grey1};
  display: flex;
  flex: 2;
`;

const TitleItem = styled.div`
  font-size: 1.2rem;
  color: ${Colors.Grey1};
  display: flex;
  flex: 1;
`;

const RowItem = styled.div`
  font-size: 1.4rem;
  color: ${Colors.Grey1};
  display: flex;
  flex: 1;
`;

const ReportHeaderContainer = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0px 30px;
  min-width: 750px;
`;

const ReportHeaderSection = styled.div`
  margin-right: 40px;
`;

const ReportHeaderTitle = styled.div`
  font-weight: 500;
  font-size: 1.2rem;
  color: ${Colors.Grey3};
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const ReportHeaderItem = styled.div`
  font-weight: 600;
  font-size: 1.4rem;
  color: ${Colors.Grey1};
`;

const Border = styled.div`
  height: 1px;
  width: 100%;
  background: ${Colors.Grey6};
`;

export const ButtonContainer = styled.div`
  display: flex;
  @media print {
    display: none;
  }
`;
type ReportHeaderProps = {
  event: any;
  firstDay?: string;
  lastDay?: string;
};
const ReportHeader: React.FC<ReportHeaderProps> = ({ event, firstDay, lastDay }) => (
  <ReportHeaderContainer>
    <ReportHeaderSection>
      <ReportHeaderTitle>
        EVENT
      </ReportHeaderTitle>
      <ReportHeaderItem>
        {event?.name || 'No Event Name'}
      </ReportHeaderItem>
    </ReportHeaderSection>
    <ReportHeaderSection>
      <ReportHeaderTitle>
        DATE
      </ReportHeaderTitle>
      <ReportHeaderItem> {event?.isMultipleDays ? (firstDay + " - " + lastDay) || 'No Event Date' : Time.format(event?.schedule?.startsAt, 'ddd, MMM DD, YYYY [at] h:mma', event?.venue?.address?.timezone) || 'No Event Date'}
      </ReportHeaderItem>
    </ReportHeaderSection>
    <ReportHeaderSection>
      <ReportHeaderTitle>
        VENUE
      </ReportHeaderTitle>
      <ReportHeaderItem>
        {event?.venue?.name || 'No Venue'}
      </ReportHeaderItem>
    </ReportHeaderSection>
    <ReportHeaderSection>
      <ReportHeaderTitle>
        ORGANIZER
      </ReportHeaderTitle>
      <ReportHeaderItem>
        {event?.organization?.orgName || 'No Organizer'}
      </ReportHeaderItem>
    </ReportHeaderSection>
  </ReportHeaderContainer>
);

type SectionHeaderProps = {
  title: string;
  qtySold?: boolean;
  refundTickets?: boolean;
  refundUpgrades?: boolean;
  comps?: boolean;
  qtyNet?: boolean;
  grossSales?: boolean;
  refunds?: boolean;
  netSales?: boolean;
  scannedCount?: boolean
};

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  qtySold,
  refundTickets,
  refundUpgrades,
  comps,
  qtyNet,
  grossSales,
  refunds,
  netSales,
  scannedCount
}) => (
  <SectionHeaderStyle>
    <Title>{title}</Title>
    <TitleItem>{qtySold ? 'QTY SOLD' : null}</TitleItem>
    <TitleItem>{refundTickets ? 'QTY REFUNDS' : null}{refundUpgrades ? 'QTY REFUNDS' : null}</TitleItem>
    <TitleItem>{comps ? 'QTY COMPS' : null}</TitleItem>
    <TitleItem>{qtyNet ? 'QTY NET' : null}</TitleItem>
    <TitleItem>{scannedCount ? 'QTY SCANNED' : null}</TitleItem>
    <TitleItem>{grossSales ? 'GROSS SALES' : null}</TitleItem>
    <TitleItem>{refunds ? 'REFUNDS' : null}</TitleItem>
    <TitleItem>{netSales ? 'NET SALES' : null}</TitleItem>
  </SectionHeaderStyle>
);

type SectionRowProps = {
  bold?: boolean;
  title?: string;
  qtySold?: string;
  grossSales?: string;
  refunds?: string;
  netSales?: string;
  refundTickets?: string;
  refundUpgrades?: string;
  comps?: string;
  qtyNet?: string;
  scannedCounts?: any
  isTierRow?: boolean;
};

const TitleCell = styled(RowItem)`
  font-weight: bold;
  flex: 2;
  padding-right: 16px;
`;

const IndentedTitle = styled.span<{ $isTierRow?: boolean }>`
  display: inline-block;
  padding-left: ${({ $isTierRow }) => ($isTierRow ? '15px' : '0px')};
  font-weight: ${({ $isTierRow }) => ($isTierRow ? 'normal' : 'bold')};
`;
const SectionRow: React.FC<SectionRowProps> = ({
  bold,
  title,
  qtySold,
  grossSales,
  refunds,
  netSales,
  refundTickets,
  refundUpgrades,
  comps,
  qtyNet,
  scannedCounts,
  isTierRow
}) => (
  <SectionRowStyle bold={bold}>
    <TitleCell>
  <IndentedTitle $isTierRow={isTierRow}>{title}</IndentedTitle>
    </TitleCell>
    <RowItem>{qtySold || null}</RowItem>
    <RowItem>{refundTickets || null}{refundUpgrades || null}</RowItem>
    <RowItem>{comps || null}</RowItem>
    <RowItem>{qtyNet || null}</RowItem>
    <RowItem>{scannedCounts}</RowItem>
    <RowItem>{grossSales || null}</RowItem>
    <RowItem>{refunds || null}</RowItem>
    <RowItem>{netSales || null}</RowItem>
  </SectionRowStyle>
);

interface IEventReportItem {
  name: string;
  grossSales: number;
  qtySold: number;
  refundTickets: number;
  refunds: number;
  netSales: number;
  comps: number;
  qtyNet: number;
  refundUpgrades: number;
  type?: string;
  scannedCount?: any
}

type EventReportMap = Record<string, IEventReportItem>;
type TicketTypeReportMap = Record<string, IEventReportItem & { tiers: EventReportMap }>

type TierDisplayRow = IEventReportItem & {
  key: string;
  title: string;
  tierId?: string;
  price?: number;
};

const normalizeTierName = (name?: string) => (name || "").trim().toLowerCase();

const formatTierPrice = (priceInCents: number): string => {
  const dollarAmt = priceInCents / 100;
  return priceInCents % 100 === 0 ? `$${dollarAmt}` : `$${dollarAmt.toFixed(2)}`;
};

const hasTierActivity = (tier: IEventReportItem) => (
  tier.qtySold > 0 ||
  tier.refundTickets > 0 ||
  tier.comps > 0 ||
  (tier.scannedCount || 0) > 0 ||
  tier.grossSales > 0 ||
  tier.refunds > 0 ||
  tier.netSales > 0
);

const mergeTierRowsForDisplay = (
  ticketName: string,
  tiers: EventReportMap,
  ticketType?: ITicketType
): TierDisplayRow[] => {
  const ADVANCE_TIER_NAME = 'advance';
  const mergedRows: Record<string, TierDisplayRow> = {};

  const firstTier = ticketType?.tiers?.[0];
  const isSingleActiveAdvanceTier = ticketType?.tiers?.length === 1 &&
    normalizeTierName(firstTier?.name) === ADVANCE_TIER_NAME &&
    firstTier?.isActive;

  const activeTierNamesWithActivity = new Set<string>();
  const uniquePricesByTierName: Record<string, Set<number>> = {};

  Object.keys(tiers).forEach((key: string) => {
    const tierItem = tiers[key];
    if (hasTierActivity(tierItem)) {
      const normName = normalizeTierName(tierItem?.name);
      activeTierNamesWithActivity.add(normName);

      if (!uniquePricesByTierName[normName]) {
        uniquePricesByTierName[normName] = new Set<number>();
      }
      const lastHyphenIdx = key.lastIndexOf('-');
      if (lastHyphenIdx !== -1) {
        const priceString = key.substring(lastHyphenIdx + 1);
        const parsedPrice = priceString !== '' ? Number(priceString) : NaN;
        if (Number.isFinite(parsedPrice)) {
          uniquePricesByTierName[normName].add(parsedPrice);
        }
      }
    }
  });

  Object.keys(tiers).forEach((tierKey: string) => {
    
    const tier = tiers[tierKey];
    const lastHyphenIndex = tierKey.lastIndexOf('-');
    const tierId = lastHyphenIndex !== -1 ? tierKey.substring(0, lastHyphenIndex) : tierKey;
    const priceStr = lastHyphenIndex !== -1 ? tierKey.substring(lastHyphenIndex + 1) : undefined;
    const price = priceStr !== undefined && priceStr !== '' ? Number(priceStr) : 0;

    const tierLabel = tier?.name;
    const normalizedTierName = normalizeTierName(tierLabel);

    const tierData = ticketType?.tiers.find(t => t._id === tierId || (t as any).id === tierId);
    const isCurrentlyActive = tierData?.isActive ?? false;

    //TODO Rule: if tier.length is > 1 & name is Advance & isActive false no need to show this.
    const isInactiveAdvanceTierWithMultipleTiers = (ticketType?.tiers && ticketType.tiers.length > 1) &&
      normalizedTierName === ADVANCE_TIER_NAME &&
      !isCurrentlyActive;

    const hasActivity = hasTierActivity(tier);
    if (!hasActivity) {
      if (isInactiveAdvanceTierWithMultipleTiers) {
        return;
      }
      const tierNameHasAnyActivity = activeTierNamesWithActivity.has(normalizedTierName);
      if (tierNameHasAnyActivity || !isCurrentlyActive) {
        return;
      }
    }
    const uniquePricesForThisTier = uniquePricesByTierName[normalizedTierName] || new Set<number>();
    const hasPriceChange = uniquePricesForThisTier.size > 1;

    const showPriceSuffix = hasActivity && (!isSingleActiveAdvanceTier || hasPriceChange);
    const formattedPrice = (priceStr !== undefined && showPriceSuffix) ? formatTierPrice(price) : '';
    const displayTitle = formattedPrice ? `${ticketName} - ${tierLabel} (${formattedPrice})` : `${ticketName} - ${tierLabel}`;
    const rowKey = showPriceSuffix ? `${normalizedTierName}-${price}` : normalizedTierName;

    if (!mergedRows[rowKey]) {
      mergedRows[rowKey] = {
        ...tier,
        key: rowKey,
        title: displayTitle,
        tierId,
        price,
      };
      return;
    }

    mergedRows[rowKey].grossSales += tier.grossSales;
    mergedRows[rowKey].qtySold += tier.qtySold;
    mergedRows[rowKey].refundTickets += tier.refundTickets;
    mergedRows[rowKey].refundUpgrades += tier.refundUpgrades;
    mergedRows[rowKey].comps += tier.comps;
    mergedRows[rowKey].qtyNet += tier.qtyNet;
    mergedRows[rowKey].refunds += tier.refunds;
    mergedRows[rowKey].netSales += tier.netSales;
    mergedRows[rowKey].scannedCount = (mergedRows[rowKey].scannedCount || 0) + (tier.scannedCount || 0);
  });

  const rows = Object.values(mergedRows);
  //Index of Ticket-tier 
  rows.sort((a, b) => {
    const idxA = ticketType?.tiers?.findIndex(t => t._id === a.tierId || (t as any).id === a.tierId) ?? -1;
    const idxB = ticketType?.tiers?.findIndex(t => t._id === b.tierId || (t as any).id === b.tierId) ?? -1;

    if (idxA !== idxB) {
      if (idxA >= 0 && idxB >= 0) return idxA - idxB;
      if (idxA >= 0) return -1;
      if (idxB >= 0) return 1;
    }

    return (b.price || 0) - (a.price || 0);
  });

  return rows;
}


const makeTicketTypesMap = (ticketTypes: ITicketType[]): TicketTypeReportMap => {
  return ticketTypes.reduce((ticketCulm: TicketTypeReportMap, ticketType: ITicketType) => {
    ticketCulm[ticketType._id as string] = {
      name: ticketType.name,
      grossSales: 0,
      qtySold: 0,
      refunds: 0,
      netSales: 0,
      refundTickets: 0,
      comps: 0,
      qtyNet: 0,
      tiers: makeTiersMap(ticketType.tiers),
      refundUpgrades: 0,
      type: "",
      scannedCount: 0
    };
    return ticketCulm;
  }, {});
}

const makeTiersMap = (tiers: ITicketTier[]): EventReportMap => {
  return tiers.reduce((tierCulm: EventReportMap, tier: ITicketTier) => {
    const key = `${tier._id}-${tier.price || 0}`;
    tierCulm[key] = {
      name: tier.name,
      grossSales: 0,
      qtySold: 0,
      refundTickets: 0,
      comps: 0,
      qtyNet: 0,
      refunds: 0,
      netSales: 0,
      refundUpgrades: 0,
      type: "",
      scannedCount: 0
    };
    return tierCulm;
  }, {});
};

const makeUpgradeTypesMap = (upgrades: IEventUpgrade[]): EventReportMap => {
  return upgrades.reduce((upgradeCulm: EventReportMap, upgrade: IEventUpgrade) => {
    upgradeCulm[upgrade._id as string] = {
      name: upgrade.name,
      grossSales: 0,
      qtySold: 0,
      refundTickets: 0,
      comps: 0,
      qtyNet: 0,
      refunds: 0,
      netSales: 0,
      refundUpgrades: 0,
      type: "",
      scannedCount: 0
    };
    return upgradeCulm;
  }, {});
}

type RenderRowsProps = {
  event: IEvent;
  orders: IOrder[];
};

const RenderRows: React.FC<RenderRowsProps> = ({ event, orders }) => {
  const { ticketTypes, upgrades } = event;
  
  const ticketMap: TicketTypeReportMap = (makeTicketTypesMap(ticketTypes as ITicketType[]));
  const upgradeMap: EventReportMap = (makeUpgradeTypesMap(upgrades as IEventUpgrade[]));

  let tax = 0 as number;

  const addSalesTax = (price: any) => {
    const netPrice = price
    return Price.output(netPrice.toFixed(2), true);
  }

  const ticketSummary = {
    name: "Ticket Summary",
    grossSales: 0,
    qtySold: 0,
    refundTickets: 0,
    comps: 0,
    qtyNet: 0,
    refunds: 0,
    netSales: 0,
    scannedCount: 0
  };

  const upgradeSummary = {
    name: "Add-ons Summary",
    grossSales: 0,
    qtySold: 0,
    comps: 0,
    qtyNet: 0,
    refunds: 0,
    netSales: 0,
    refundUpgrades: 0,
    scannedCount: 0
  };

  // Fees summary to track taxes and fees separately
  const feesSummary = {
    salesTax: { gross: 0, refunded: 0, net: 0 },
    selloutFees: { gross: 0, refunded: 0, net: 0 },
    stripeFees: { gross: 0, refunded: 0, net: 0 },
    organizationFees: { gross: 0, refunded: 0, net: 0 }
  };

  orders.forEach((order: IOrder) => {
    if (order.fees) {
      tax = order?.fees?.filter((a: any) => a.name === EventSaleTaxEnum.SalesTax)[0]?.value
    }
    // loop through order tickets determining values
    order.tickets.forEach((ticket: IOrderTicket) => {
      const ticketPrice = order.type === OrderTypeEnum.RSVP ? Number(ticket?.values) || 0 : ticket.price;
      const includingTax = tax > 0 ? (ticketPrice / 100) * tax + ticketPrice : ticketPrice;
      // const includingTax = tax > 0 ? ticket.price / 100 * tax + ticket.price : ticket.price;
      // fail safe for ticket that is not on event but is somehow in orders
      if ((!ticketMap.hasOwnProperty(ticket.ticketTypeId))) {
        ticketMap[ticket.ticketTypeId] = {
          name: ticket.name,
          grossSales: 0,
          qtySold: 0,
          comps: 0,
          qtyNet: 0,
          refunds: 0,
          netSales: 0,
          refundTickets: 0,
          refundUpgrades: 0,
          scannedCount: 0,
          tiers: {} as EventReportMap,
          type: order.type
        };
        return;
      }

      // ticket type (using base price without tax)
      ticketMap[ticket.ticketTypeId].grossSales += ticketPrice;
      ticketMap[ticket.ticketTypeId].qtySold += 1;
      ticketMap[ticket.ticketTypeId].refunds += ticket.refund.refunded ? ticketPrice : 0;
      ticketMap[ticket.ticketTypeId].netSales += ticketPrice - (ticket.refund.refunded ? ticketPrice : 0);
      ticketMap[ticket.ticketTypeId].refundTickets += ticket.refund.refunded ? 1 : 0;
      // ticketMap[ticket.ticketTypeId].comps += order.type === OrderTypeEnum.Complimentary ? 1 : 0;
      ticketMap[ticket.ticketTypeId].comps += (order.type === OrderTypeEnum.Complimentary && !ticket.refund.refunded) ? 1 : 0;
      ticketMap[ticket.ticketTypeId].qtyNet += 1 - (ticket.refund.refunded ? 1 : 0);

      let totalCount = 0;
      const totalScannedTierCount: Record<string, number> = {};
      for (const orderItem of orders) {
        for (const ticket of orderItem.tickets) {
          const tPrice = orderItem.type === OrderTypeEnum.RSVP ? Number(ticket?.values) || 0 : ticket.price;
          const tType = (ticketTypes as ITicketType[]).find(tt => tt._id === ticket.ticketTypeId);
          const currentTier = tType?.tiers.find(t => t._id === ticket.ticketTierId || (t as any).id === ticket.ticketTierId);
          const groupingPrice = orderItem.type === OrderTypeEnum.Complimentary
            ? (currentTier?.price ?? 0)
            : (ticket.origionalPrice !== undefined && ticket.origionalPrice !== null ? ticket.origionalPrice : tPrice);
          const tierKey = `${ticket.ticketTierId}-${groupingPrice}`;
          totalScannedTierCount[tierKey] = totalScannedTierCount[tierKey] || 0;
          for (const scannedTicket of ticket.scan || []) {
            if (scannedTicket.scanned === true) {
              totalScannedTierCount[tierKey] += 1;
              totalCount += 1;
            }
          }
        }
      }
      
      for (const tierKey in totalScannedTierCount) {
        if (Object.prototype.hasOwnProperty.call(totalScannedTierCount, tierKey)) {
          Object.keys(ticketMap).forEach(ticketTypeId => {
            if (ticketMap[ticketTypeId].tiers[tierKey]) {
              ticketMap[ticketTypeId].tiers[tierKey].scannedCount = totalScannedTierCount[tierKey];
            }
          });
        }
      }
      
      Object.keys(ticketMap).forEach(ticketTypeId => {
        let parentScannedCount = 0;
        Object.keys(ticketMap[ticketTypeId].tiers).forEach(tierKey => {
          parentScannedCount += ticketMap[ticketTypeId].tiers[tierKey].scannedCount || 0;
        });
        ticketMap[ticketTypeId].scannedCount = parentScannedCount;
      });
      ticketSummary.scannedCount = totalCount;
      // TODO Without tier count calculations
      // for (const orderItem of orders) {
      //   const order = orderItem;
      //   for (const ticket of order.tickets) {
      //     const ticketTypeId = ticket.ticketTypeId;
      //     totalScannedCount[ticketTypeId] = totalScannedCount[ticketTypeId] || 0;
      //     for (const scannedTicket of ticket.scan || []) {
      //       if (scannedTicket.scanned === true) {
      //         const scannedNumber = Number(scannedTicket.scanned);
      //         totalScannedCount[ticketTypeId] += scannedNumber;
      //       }
      //       if (scannedTicket.scanned === true) {
      //         totalCount++
      //       }
      //     }
      //   }
      // }

      // for (const ticketTypeId in totalScannedCount) {
      //   if (Object.prototype.hasOwnProperty.call(totalScannedCount, ticketTypeId)) {
      //     ticketMap[ticketTypeId].scannedCount = totalScannedCount[ticketTypeId];
      //   }
      // }
      // ticket tier
      const tType = (ticketTypes as ITicketType[]).find(tt => tt._id === ticket.ticketTypeId);
      const currentTier = tType?.tiers.find(t => t._id === ticket.ticketTierId || (t as any).id === ticket.ticketTierId);
      const groupingPrice = order.type === OrderTypeEnum.Complimentary
        ? (currentTier?.price ?? 0)
        : (ticket.origionalPrice !== undefined && ticket.origionalPrice !== null ? ticket.origionalPrice : ticketPrice);
      const tierKey = `${ticket.ticketTierId}-${groupingPrice}`;
      if (typeof (ticketMap[ticket.ticketTypeId].tiers[tierKey]) == 'undefined') {
        const tierName = currentTier?.name || "Advance";
        ticketMap[ticket.ticketTypeId].tiers[tierKey] = { grossSales: 0, type: "", qtySold: 0, refundTickets: 0, refundUpgrades: 0, comps: 0, qtyNet: 0, refunds: 0, netSales: 0, name: tierName, scannedCount: 0 };
      }
      ticketMap[ticket.ticketTypeId].tiers[tierKey].grossSales += ticketPrice;
      ticketMap[ticket.ticketTypeId].tiers[tierKey].qtySold += 1;
      ticketMap[ticket.ticketTypeId].tiers[tierKey].refunds += ticket.refund.refunded ? ticketPrice : 0;
      ticketMap[ticket.ticketTypeId].tiers[tierKey].netSales += ticketPrice - (ticket.refund.refunded ? ticketPrice : 0);
      ticketMap[ticket.ticketTypeId].tiers[tierKey].refundTickets += ticket.refund.refunded ? 1 : 0;
      ticketMap[ticket.ticketTypeId].tiers[tierKey].comps += (order.type === OrderTypeEnum.Complimentary && !ticket.refund.refunded) ? 1 : 0;

      // tickets summary (using base price without tax)
      ticketSummary.grossSales += ticketPrice;
      ticketSummary.qtySold += 1;
      ticketSummary.refunds += ticket.refund.refunded ? ticketPrice : 0;
      ticketSummary.netSales += ticketPrice - (ticket.refund.refunded ? ticketPrice : 0);
      ticketSummary.refundTickets += ticket.refund.refunded ? 1 : 0;
      // ticketSummary.comps += order.type === OrderTypeEnum.Complimentary ? 1 : 0;
      ticketSummary.comps += (order.type === OrderTypeEnum.Complimentary && !ticket.refund.refunded) ? 1 : 0;
      ticketSummary.scannedCount = totalCount

    });

    // loop through order upgrades determining values
    order.upgrades.forEach((upgrade: any) => {
      // fail safe for upgrade that is not on event but is somehow in orders
      if (!upgradeMap[upgrade.upgradeId]) {
        upgradeMap[upgrade.upgradeId] = {
          name: upgrade.name,
          grossSales: 0,
          qtySold: 0,
          refundTickets: 0,
          comps: 0,
          qtyNet: 0,
          refunds: 0,
          netSales: 0,
          refundUpgrades: 0,
          type: "",
          scannedCount: 0
        };
      }
      // upgrade type
      upgradeMap[upgrade.upgradeId].grossSales += upgrade.price;
      upgradeMap[upgrade.upgradeId].qtySold += 1;
      upgradeMap[upgrade.upgradeId].refunds += upgrade.refund.refunded ? upgrade.price : 0;
      upgradeMap[upgrade.upgradeId].netSales += upgrade.price - (upgrade.refund.refunded ? upgrade.price : 0);
      upgradeMap[upgrade.upgradeId].refundUpgrades += upgrade.refund.refunded ? 1 : 0;
      // upgradeMap[upgrade.upgradeId].comps += order.type === OrderTypeEnum.Complimentary ? 1 : 0;
      upgradeMap[upgrade.upgradeId].comps += (order.type === OrderTypeEnum.Complimentary && !upgrade.refund.refunded) ? 1 : 0;
      upgradeMap[upgrade.upgradeId].qtyNet += 1 - (upgrade.refund.refunded ? upgrade.price : 0);
      
      const totalScannedUpgrade = {};
      let totalUpgradeCount = 0;
      
      for (const orderItem of orders) {
          const order = orderItem; 
          for (const upgrade of order.upgrades) {
              const upgradeId = upgrade.upgradeId;
              if (!totalScannedUpgrade[upgradeId]) {
                  totalScannedUpgrade[upgradeId] = 0;
              }
              if (upgrade.scan && upgrade.scan.scanned === true) {
                  const scannedNumber = Number(upgrade.scan.scanned);
                  totalScannedUpgrade[upgradeId] += scannedNumber;
                  totalUpgradeCount++;
              }
          }
      }
      for (const upgradeId in totalScannedUpgrade) {
          if (Object.prototype.hasOwnProperty.call(totalScannedUpgrade, upgradeId)) {
              if (upgradeMap[upgradeId]) {
                  upgradeMap[upgradeId].scannedCount = totalScannedUpgrade[upgradeId];
              }
          }
      }
      
      //upgrade summary
      upgradeSummary.grossSales += upgrade.price;
      upgradeSummary.qtySold += 1;
      upgradeSummary.refunds += upgrade.refund.refunded ? upgrade.price : 0;
      upgradeSummary.netSales += upgrade.price - (upgrade.refund.refunded ? upgrade.price : 0);
      upgradeSummary.refundUpgrades += upgrade.refund.refunded ? 1 : 0;
      // upgradeSummary.comps += order.type === OrderTypeEnum.Complimentary ? 1 : 0;
      upgradeSummary.comps += (order.type === OrderTypeEnum.Complimentary && !upgrade.refund.refunded) ? 1 : 0;
      upgradeSummary.scannedCount = totalUpgradeCount
    });
    if (order.type !== OrderTypeEnum.Complimentary && order.type !== OrderTypeEnum.RSVP && order.fees?.length > 0) {
      // Check if fees were refunded (separate from ticket refunds)
      const promoterFeeRefunded = order.promoterFee?.refund?.refunded === true;
      const processingFeeRefunded = order.processingFee?.refund?.refunded === true;

      order.fees.forEach((fee: IFee) => {
        const amount = Number(fee.amount) || 0;

        // 1. Sales Tax
        if (fee.name?.trim().toLowerCase() === String(EventSaleTaxEnum.SalesTax).toLowerCase()) {
          feesSummary.salesTax.gross += amount;
          return;
        }

        // 2. Sellout Fees
        if (fee.appliedBy === FeeAppliedByEnum.Sellout) {
          feesSummary.selloutFees.gross += amount;
          return;
        }

        // 3. Stripe Fees
        if (fee.appliedBy === FeeAppliedByEnum.Stripe) {
          feesSummary.stripeFees.gross += amount;
          return;
        }

        // 4. Organization/Promoter Fees
        if (fee.appliedBy === FeeAppliedByEnum.Organization) {
          feesSummary.organizationFees.gross += amount;
        }
      });

      if (promoterFeeRefunded) {
        feesSummary.salesTax.refunded += order.promoterFee?.refund?.salesTaxFee || 0;
        feesSummary.organizationFees.refunded += order.promoterFee?.refund?.promoterFee || 0;
      }

      if (processingFeeRefunded) {
        feesSummary.selloutFees.refunded += order.processingFee?.refund?.selloutFee || 0;
        feesSummary.stripeFees.refunded += order.processingFee?.refund?.stripeFee || 0;
      }
    }
  });

  // Calculate net fees
  feesSummary.salesTax.net = feesSummary.salesTax.gross - feesSummary.salesTax.refunded;
  feesSummary.selloutFees.net = feesSummary.selloutFees.gross - feesSummary.selloutFees.refunded;
  feesSummary.stripeFees.net = feesSummary.stripeFees.gross - feesSummary.stripeFees.refunded;
  feesSummary.organizationFees.net = feesSummary.organizationFees.gross - feesSummary.organizationFees.refunded;

  // Check if any fees exist
  const hasFees = feesSummary?.salesTax.gross > 0 || 
                  feesSummary?.selloutFees.gross > 0 || 
                  feesSummary?.stripeFees.gross > 0 || 
                  feesSummary?.organizationFees.gross > 0;

  // Total fees for summary
  const totalFees = {
    gross: feesSummary.salesTax.gross + feesSummary.selloutFees.gross + feesSummary.stripeFees.gross + feesSummary.organizationFees.gross,
    refunded: feesSummary.salesTax.refunded + feesSummary.selloutFees.refunded + feesSummary.stripeFees.refunded + feesSummary.organizationFees.refunded,
    net: feesSummary.salesTax.net + feesSummary.selloutFees.net + feesSummary.stripeFees.net + feesSummary.organizationFees.net
  };

  const totalSummary = {
    grossSales: ticketSummary.grossSales + upgradeSummary.grossSales + totalFees.gross,
    refunds: ticketSummary.refunds + upgradeSummary.refunds + totalFees.refunded,
    netSales: ticketSummary.netSales + upgradeSummary.netSales + totalFees.net,
  };

  return (
    <>
      <SectionHeader title="TICKETS" qtySold refundTickets comps qtyNet grossSales refunds netSales scannedCount />
      {Object.keys(ticketMap).map((ticket: any, index: number) => {
        let tiers = ticketMap[ticket].tiers;
        const eventTicketTypes = ticketTypes as ITicketType[];
        const ticketType = eventTicketTypes?.find(tt => tt._id === ticket);
        const tierRows = mergeTierRowsForDisplay(ticketMap[ticket].name, tiers, ticketType);
        const showTiers = tierRows.length > 1;

        return (
          <Fragment key={index}>
            <SectionRow
              bold
              title={ticketMap[ticket].name}
              qtySold={ticketMap[ticket].qtySold.toString()}
              grossSales={`$${addSalesTax(ticketMap[ticket].grossSales)}`}
              refunds={`$${addSalesTax(ticketMap[ticket].refunds)}`}
              netSales={`$${addSalesTax(ticketMap[ticket].netSales)}`}
              refundTickets={ticketMap[ticket].refundTickets.toString()}
              qtyNet={(ticketMap[ticket].qtySold - ticketMap[ticket].refundTickets - ticketMap[ticket].comps).toString()}
              comps={ticketMap[ticket].comps.toString()}
              scannedCounts={ticketMap[ticket].scannedCount}
            />
            {showTiers && tierRows.map((tier, tierIdx: number) => {
              return (
                <SectionRow
                  key={tier.key || tierIdx}
                  title={tier.title}
                  isTierRow={true}
                  qtySold={tier.qtySold.toString()}
                  grossSales={`$${addSalesTax(tier.grossSales)}`}
                  refunds={`$${addSalesTax(tier.refunds)}`}
                  netSales={`$${addSalesTax(tier.netSales)}`}
                  refundTickets={tier.refundTickets.toString()}
                  qtyNet={(tier.qtySold - tier.refundTickets - tier.comps).toString()}
                  comps={tier.comps.toString()}
                  scannedCounts={tier.scannedCount}
                />
              );
            })}
            <Border />
          </Fragment>
        );
      })}

      <SectionRow
        bold
        title={ticketSummary.name}
        qtySold={ticketSummary.qtySold.toString()}
        refundTickets={ticketSummary.refundTickets.toString()}
        comps={ticketSummary.comps.toString()}
        netSales={`$${addSalesTax(ticketSummary.netSales)}`}
        grossSales={`$${addSalesTax(ticketSummary.grossSales)}`}
        refunds={`$${addSalesTax(ticketSummary.refunds)}`}
        qtyNet={(ticketSummary.qtySold - ticketSummary.refundTickets - ticketSummary.comps).toString()}
        scannedCounts={ticketSummary.scannedCount}
      />
      <SectionHeader title="ADD-ONS" qtySold refundUpgrades comps qtyNet grossSales refunds netSales scannedCount />
      {Object.keys(upgradeMap).map((upgrade: any, index: number) => {

        return (
          <Fragment key={index}>
            <SectionRow
              title={upgradeMap[upgrade].name}
              comps={upgradeMap[upgrade].comps.toString()}
              qtySold={upgradeMap[upgrade].qtySold.toString()}
              grossSales={`$${addSalesTax(upgradeMap[upgrade].grossSales)}`}
              refunds={`$${addSalesTax(upgradeMap[upgrade].refunds)}`}
              netSales={`$${addSalesTax(upgradeMap[upgrade].netSales)}`}
              refundUpgrades={upgradeMap[upgrade].refundUpgrades.toString()}
              qtyNet={(upgradeMap[upgrade].qtySold - upgradeMap[upgrade].refundUpgrades - upgradeMap[upgrade].comps).toString()}
              scannedCounts={upgradeMap[upgrade].scannedCount}

            />
            <Border />
          </Fragment>
        );
      })}
      <SectionRow
        bold
        title={upgradeSummary.name}
        qtySold={upgradeSummary.qtySold.toString()}
        refundTickets={upgradeSummary.refundUpgrades.toString()}
        comps={upgradeSummary.comps.toString()}
        qtyNet={(upgradeSummary.qtySold - upgradeSummary.refundUpgrades - upgradeSummary.comps).toString()}
        grossSales={`$${addSalesTax(upgradeSummary.grossSales)}`}
        refunds={`$${addSalesTax(upgradeSummary.refunds)}`}
        netSales={`$${addSalesTax(upgradeSummary.netSales)}`}
        scannedCounts={upgradeSummary.scannedCount}
      />
      <Border />
      <SectionRow
        bold
        title="TOTAL TICKET & ADD-ONS"
        qtySold={(ticketSummary.qtySold + upgradeSummary.qtySold).toString()}
        refundTickets={(ticketSummary.refundTickets + upgradeSummary.refundUpgrades).toString()}
        comps={(ticketSummary.comps + upgradeSummary.comps).toString()}
        qtyNet={((ticketSummary.qtySold - ticketSummary.refundTickets - ticketSummary.comps) + (upgradeSummary.qtySold - upgradeSummary.refundUpgrades - upgradeSummary.comps)).toString()}
        grossSales={`$${addSalesTax(ticketSummary.grossSales + upgradeSummary.grossSales)}`}
        refunds={`$${addSalesTax(ticketSummary.refunds + upgradeSummary.refunds)}`}
        netSales={`$${addSalesTax(ticketSummary.netSales + upgradeSummary.netSales)}`}
        scannedCounts={ticketSummary.scannedCount + upgradeSummary.scannedCount}
      />
      {/* Taxes & Fees Section */}
      {hasFees && (
        <>
          <SectionHeader title="TAXES & ADDITIONAL FEES" grossSales refunds netSales />
          {feesSummary.salesTax.gross > 0 && (
            <>
              <SectionRow
                title="Sales Tax"
                grossSales={`$${addSalesTax(feesSummary.salesTax.gross)}`}
                refunds={`$${addSalesTax(feesSummary.salesTax.refunded)}`}
                netSales={`$${addSalesTax(feesSummary.salesTax.net)}`}
              />
              <Border />
            </>
          )}
          {feesSummary.selloutFees.gross > 0 && (
            <>
              <SectionRow
                title="Sellout Fees"
                grossSales={`$${addSalesTax(feesSummary.selloutFees.gross)}`}
                refunds={`$${addSalesTax(feesSummary.selloutFees.refunded)}`}
                netSales={`$${addSalesTax(feesSummary.selloutFees.net)}`}
              />
              <Border />
            </>
          )}
          {feesSummary.stripeFees.gross > 0 && (
            <>
              <SectionRow
                title="Stripe Fees"
                grossSales={`$${addSalesTax(feesSummary.stripeFees.gross)}`}
                refunds={`$${addSalesTax(feesSummary.stripeFees.refunded)}`}
                netSales={`$${addSalesTax(feesSummary.stripeFees.net)}`}
              />
              <Border />
            </>
          )}
          {feesSummary.organizationFees.gross > 0 && (
            <>
              <SectionRow
                title="Organization Fees"
                grossSales={`$${addSalesTax(feesSummary.organizationFees.gross)}`}
                refunds={`$${addSalesTax(feesSummary.organizationFees.refunded)}`}
                netSales={`$${addSalesTax(feesSummary.organizationFees.net)}`}
              />
              <Border />
            </>
          )}
        </>
      )}
      <SectionHeader title="TOTAL SALES" grossSales refunds netSales />
      <SectionRow
        grossSales={`$${addSalesTax(totalSummary.grossSales)}`}
        refunds={`$${addSalesTax(totalSummary.refunds)}`}
        netSales={`$${addSalesTax(totalSummary.netSales)}`}
      />
    </>
  );
};

type EventReportsProps = {};
const EventReports: React.FC<EventReportsProps> = () => {
  /* Hooks */
  const dispatch = useDispatch();
  const { event } = useEvent();
  const { data } = useQuery(QUERY_ORDERS, {
    fetchPolicy: 'network-only',
    variables: {
      query: {
        eventIds: [event?._id],
      },
    }
  });

  const [channelFilter, setChannelFilter] = React.useState<string>('All');
  const [paymentTypeFilter, setPaymentTypeFilter] = React.useState<string>('All');

  const allOrders = data?.orders || [];
  const activeOrders = allOrders.filter((order: IOrder) => order.state !== OrderStateEnum.Canceled);
  
  const updatedActiveOrders = activeOrders.map((order: any) => {
    const filteredTickets = order.tickets?.filter((ticket: any) => ticket.parentTicketId === "" || ticket.parentTicketId === null) || [];
    const filteredUpgrades = order.upgrades?.filter((upgrade: any) => upgrade.parentUpgradeId === "" || upgrade.parentUpgradeId === null) || [];
    return {
      ...order,
      tickets: filteredTickets, 
      upgrades: filteredUpgrades,
    };
  });

  const filteredOrders = updatedActiveOrders.filter((order: any) => {
    if (channelFilter !== 'All' && order.channel !== channelFilter) {
      return false;
    }

    if (paymentTypeFilter !== 'All') {
      const hasMatchingPayment = order.payments?.some((p: any) => p.paymentMethodType === paymentTypeFilter);
      if (paymentTypeFilter === PaymentMethodTypeEnum.None) {
        const isComp = order.type === OrderTypeEnum.Complimentary;
        if (!hasMatchingPayment && !isComp) return false;
      } else {
        if (!hasMatchingPayment) return false;
      }
    }

    return true;
  });

  const channelDisplayMap: Record<string, string> = {
    'All': 'All Channels',
    [OrderChannelEnum.Online]: 'Online',
    [OrderChannelEnum.BoxOffice]: 'Box Office',
  };

  const paymentTypeDisplayMap: Record<string, string> = {
    'All': 'All Payment Types',
    [PaymentMethodTypeEnum.CardEntry]: 'Card Entry',
    [PaymentMethodTypeEnum.CardReader]: 'Card Reader',
    [PaymentMethodTypeEnum.Cash]: 'Cash',
    [PaymentMethodTypeEnum.Check]: 'Check',
    [PaymentMethodTypeEnum.None]: 'None',
  };

  ///////////////// For Multidays ////////////////
  const firstDay = event?.schedule && Time.format(
    event?.schedule?.startsAt,
    "ddd, MMM Do [at] h:mma",
    event?.venue?.address?.timezone
  );
  const lastDay = event?.schedule && Time.format(
    event?.schedule?.endsAt,
    "ddd, MMM Do [at] h:mma",
    event?.venue?.address?.timezone
  );

  const subscribeSalesReport = () => {
    dispatch(AppActions.pushModal(ModalTypes.SubscribeSalesReport));
  };

  /* Render */
  return (
    <>
      <PageLoader nav sideNav fade={Boolean(event && data?.orders)} />
      {event && data?.orders && (
        <PaddedPage>
          <PageHeader>
            <PageTitle>
              Sales Report
            </PageTitle>
            <ButtonContainer>
              <Button
                type={ButtonTypes.Thin}
                icon={Icons.PrintRegular}
                text="Print report"
                onClick={() => window.print()}
                margin={"0 5px 0 0"}
              />
              <Button
                type={ButtonTypes.Thin}
                icon={Icons.ThumbsUpLight}
                text="Subscribe"
                onClick={() => subscribeSalesReport()}
              />
            </ButtonContainer>
          </PageHeader>
          <FiltersContainer>
            <FilterWrapper>
              <Dropdown
                value={channelDisplayMap[channelFilter]}
                width="100%"
                height='40px'
                containerMinHeight={"40px"}
                optionsHeight={"40px"}
                items={[
                  { text: 'All Channels', value: 'All' },
                  { text: 'Online', value: OrderChannelEnum.Online },
                  { text: 'Box Office', value: OrderChannelEnum.BoxOffice },
                ]}
                onChange={(val) => setChannelFilter(val)}
                itemContainerHeight={"40px"}
                valueWhiteSpace={"pre-wrap"}
                label="By channel"

              />
            </FilterWrapper>
            <Spacer/>
            <FilterWrapper>
              <Dropdown
                value={paymentTypeDisplayMap[paymentTypeFilter]}
                width="100%"
                height='40px'
                label="By Payment"
                containerMinHeight={"40px"}
                optionsHeight={"40px"}
                items={[
                  { text: 'All Payment Types', value: 'All' },
                  { text: 'Card Entry', value: PaymentMethodTypeEnum.CardEntry },
                  { text: 'Card Reader', value: PaymentMethodTypeEnum.CardReader },
                  { text: 'Cash', value: PaymentMethodTypeEnum.Cash },
                  { text: 'Check', value: PaymentMethodTypeEnum.Check },
                  { text: 'None', value: PaymentMethodTypeEnum.None },
                ]}
                onChange={(val) => setPaymentTypeFilter(val)}
                itemContainerHeight={"40px"}
                valueWhiteSpace={"pre-wrap"}
              />
            </FilterWrapper>
          </FiltersContainer>
          <TableContainer>
            <ReportHeader event={event} firstDay={firstDay} lastDay={lastDay} />
            <RenderRows event={event} orders={filteredOrders} />
          </TableContainer>
        </PaddedPage>
      )}
    </>
  );
};

export default EventReports;
